<template>
  <VueFinalModal
    :modelValue="modelValue"
    @update:modelValue="$emit('update:modelValue', $event)"
    :clickToClose="!submitting"
    :escToClose="!submitting"
    classes="documents-modal"
    content-class="confirm-modal-content modal--procure-split"
  >
    <template #default>
      <button
        class="modal__close icons-button"
        @click="close"
        :disabled="submitting"
        title="Chiudi"
      >
        <i class="fa-solid fa-xmark"></i>
      </button>

      <h3 class="modal__title">Ordina articolo</h3>
      <p class="modal__subtitle">
        <strong>{{ item?.itemName || item?.brand }}</strong>
      </p>

      <p v-if="loading" class="procure-hint">
        Caricamento preventivi in attesa…
      </p>

      <p v-else-if="loadError" class="procure-error">{{ loadError }}</p>

      <p v-else-if="pending.length === 0" class="procure-hint">
        Nessun preventivo in attesa per questo articolo.
      </p>

      <table v-else class="procure-table">
        <thead>
          <tr>
            <th>Preventivo</th>
            <th>Cliente</th>
            <th>Data</th>
            <th class="col-right">Da ordinare</th>
            <th class="col-right">Assegna</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in pending" :key="row.quoteItemId">
            <td>#{{ row.quoteId }}</td>
            <td>{{ row.clientName }}</td>
            <td>{{ formatToLocalDate(row.date) }}</td>
            <td class="col-right">{{ row.quantityToProcure }} {{ unit }}</td>
            <td class="col-right">
              <input
                type="number"
                v-model.number="allocation[row.quoteItemId]"
                :min="0"
                :max="row.quantityToProcure"
                step="1"
                :disabled="submitting"
                class="procure-input"
              />
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td colspan="4" class="col-right">
              <strong>Totale ordinato</strong>
            </td>
            <td class="col-right">
              <strong>{{ total }} {{ unit }}</strong>
            </td>
          </tr>
        </tfoot>
      </table>

      <p v-if="error" class="procure-error">{{ error }}</p>

      <div class="modal-actions">
        <button class="secondary-btn" @click="close" :disabled="submitting">
          Annulla
        </button>
        <button
          class="primary-btn confirm-btn"
          @click="confirm"
          :disabled="submitting || !isValid"
        >
          <span v-if="submitting">Ordina…</span>
          <span v-else>Ordina</span>
        </button>
      </div>
    </template>
  </VueFinalModal>
</template>

<script>
import { VueFinalModal } from "vue-final-modal";
import { mapActions } from "vuex/dist/vuex.cjs.js";
import { formatToLocalDate } from "../utilities/formatting";

export default {
  name: "WarehouseProcureModal",
  components: { VueFinalModal },
  props: {
    modelValue: { type: Boolean, default: false },
    item: { type: Object, default: null },
    unit: { type: String, default: "pz" },
  },
  emits: ["update:modelValue", "procured"],

  data() {
    return {
      pending: [],
      allocation: {},
      loading: false,
      loadError: "",
      submitting: false,
      error: "",
    };
  },

  computed: {
    total() {
      return Object.values(this.allocation).reduce(
        (sum, v) => sum + (Number(v) || 0),
        0
      );
    },

    // Ogni riga: intero fra 0 e la sua quota. Almeno una riga > 0.
    isValid() {
      if (this.pending.length === 0) return false;
      const rowsOk = this.pending.every((row) => {
        const n = Number(this.allocation[row.quoteItemId] || 0);
        return Number.isInteger(n) && n >= 0 && n <= row.quantityToProcure;
      });
      return rowsOk && this.total > 0;
    },
  },

  watch: {
    modelValue(open) {
      if (open) this.load();
    },
  },

  methods: {
    ...mapActions(["fetchPendingProcurement", "procureQuoteItems"]),

    formatToLocalDate,

    async load() {
      if (!this.item?.id) return;
      this.loading = true;
      this.loadError = "";
      this.error = "";
      this.allocation = {};
      try {
        this.pending = await this.fetchPendingProcurement(this.item.id);
        // Pre-riempimento: ogni preventivo saldato per intero.
        this.pending.forEach((row) => {
          this.allocation[row.quoteItemId] = row.quantityToProcure;
        });
      } catch (err) {
        this.loadError =
          err?.response?.data?.message ||
          "Impossibile caricare i preventivi in attesa.";
        console.error("Errore caricamento preventivi in attesa:", err);
      } finally {
        this.loading = false;
      }
    },

    close() {
      if (this.submitting) return;
      this.$emit("update:modelValue", false);
    },

    async confirm() {
      if (!this.isValid) {
        this.error = "Assegnare almeno un pezzo, senza superare le quote.";
        return;
      }

      // Le righe a 0 non vanno mandate: l'endpoint pretende quantity >= 1.
      const allocations = this.pending
        .map((row) => ({
          quoteItemId: row.quoteItemId,
          quantity: Number(this.allocation[row.quoteItemId] || 0),
        }))
        .filter((a) => a.quantity >= 1);

      this.submitting = true;
      this.error = "";
      try {
        await this.procureQuoteItems({ allocations });
        this.$toast.success(`Ordine registrato: ${this.total} ${this.unit}`);
        this.$emit("procured");
        this.$emit("update:modelValue", false);
      } catch (err) {
        this.error =
          err?.response?.data?.message || "Impossibile registrare l'ordine.";
        console.error("Errore registrazione ordine da magazzino:", err);
        // 409: qualcuno ha ordinato nel frattempo, ricarico le quote vere.
        await this.load();
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.procure-table {
  width: 100%;
  border-collapse: collapse;
  margin: 1rem 0;
  font-size: 0.85rem;
}

.procure-table th,
.procure-table td {
  padding: 0.4rem 0.5rem;
  border-bottom: 1px solid var(--border-color, #eee);
}

.col-right {
  text-align: right;
}

.procure-input {
  width: 5rem;
  padding: 0.25rem;
  text-align: right;
  border: 1px solid var(--border-color, #ccc);
  border-radius: 4px;
}

.procure-hint {
  font-size: 0.85rem;
  opacity: 0.75;
}

.procure-error {
  color: var(--danger-color, #c0392b);
  font-size: 0.85rem;
  margin-bottom: 0.5rem;
}
</style>
